import { isAlwaysAnswered } from '@lib/customization/poiRules';
import { newEntityId, type BarcodeFormat, type FormInputType, type PoiField } from '@store/api/poiTypeEndpoints';

/**
 * Edits to a field that more than one editor makes the same way: the field drawer and the
 * designer's inspector. Kept here so a type switch or a decimals switch means one thing.
 */

/**
 * Rounds whatever a numeric field is holding, for the moment decimals are turned off.
 *
 * Without this the field keeps a fractional value the API will reject, and the separator
 * keys are now disabled, so the only way out would be to clear the box and start again.
 */
export const whole = (field: PoiField): Partial<PoiField> => ({
  ...(typeof field.value === 'number' ? { value: Math.round(field.value) } : {}),
  ...(field.min === undefined ? {} : { min: Math.round(field.min) }),
  ...(field.max === undefined ? {} : { max: Math.round(field.max) }),
});

/**
 * A field's rules with every `then.required` taken out, for a switch to a type that always
 * holds an answer. The Required control is disabled for those types, so a `true` carried
 * over would be saved behind a switch that can no longer turn it off. A rule that did
 * nothing else goes with it, since the API rejects an empty `then`.
 */
const withoutRequired = (rules: PoiField['rules']): PoiField['rules'] => {
  const kept = (rules ?? [])
    .map((rule) => {
      const then = { ...rule.then };

      delete then.required;

      return { ...rule, then };
    })
    .filter((rule) => Object.keys(rule.then).length > 0);

  return kept.length ? kept : undefined;
};

/** A choice field's `value` is one id or a list of them; both read as a list here. */
export const chosenIds = (field: Pick<PoiField, 'value'>): string[] => {
  if (Array.isArray(field.value)) return field.value;

  return typeof field.value === 'string' && field.value ? [field.value] : [];
};

/**
 * The field without its choice at `index`. A default left naming it would point at nothing
 * and be rejected on save, so it goes too.
 */
export const withoutChoice = (field: PoiField, index: number): Partial<PoiField> => {
  const options = field.options ?? [];
  const kept = chosenIds(field).filter((id) => id !== options[index]?.id);

  return {
    options: options.filter((_, position) => position !== index),
    value: field.type === 'List' ? kept[0] : kept,
  };
};

/**
 * The field with one choice marked or unmarked as the default. Defaults are held by option
 * id, minted when the option is added, so this works before the first save.
 */
export const withDefaultToggled = (field: PoiField, optionId: string): Partial<PoiField> => {
  const chosen = chosenIds(field);
  const isOn = chosen.includes(optionId);

  if (field.type === 'List') return { value: isOn ? undefined : optionId };

  return { value: isOn ? chosen.filter((id) => id !== optionId) : [...chosen, optionId] };
};

/**
 * Switching type drops the previous type's configuration rather than carrying it along:
 * the API rejects a key sent against the wrong type, so keeping it would only produce a
 * 400 at save time.
 */
/** The choices a field has on becoming a choice type, and the default among them. */
const choicesOf = (current: PoiField, type: 'List' | 'MultiList'): Pick<PoiField, 'options' | 'value'> => {
  const options = current.options?.length ? current.options : [{ id: newEntityId(), label: {} }];
  // Only ids the options declare. A Text or Barcode default is a string as well, and carried over
  // as an id it names no choice: the API refuses it, and a MultiList has no control that clears it.
  const chosen = chosenIds(current).filter((id) => options.some((option) => option.id === id));

  return { options, value: type === 'List' ? chosen[0] : chosen };
};

export const withType = (current: PoiField, type: FormInputType): PoiField => ({
  label: current.label,
  help: current.help,
  placeholder: current.placeholder,
  visible: current.visible,
  required: isAlwaysAnswered(type) ? false : current.required,
  readOnly: current.readOnly,
  // Rules point at *other* fields, so they are as independent of this field's type as
  // its label is. Dropping them here would lose them with no warning. Only what they
  // would make required goes, on a type that can never be required.
  rules: isAlwaysAnswered(type) ? withoutRequired(current.rules) : current.rules,
  id: current.id,
  type,
  ...(type === 'Text' ? { multiline: false } : {}),
  ...(type === 'Number' || type === 'Range' ? { decimal: false } : {}),
  ...(type === 'Range' ? { min: 0, max: 100 } : {}),
  ...(type === 'Toggle' ? { value: false } : {}),
  ...(type === 'Barcode' ? { formats: [] as BarcodeFormat[] } : {}),
  // Switching between the two choice types keeps both the options and what was marked
  // as default; only the shape of `value` changes — one id for List, a list for MultiList.
  ...(type === 'List' || type === 'MultiList' ? choicesOf(current, type) : {}),
});
